import React, { useEffect } from "react";
import { Card, CardBody, Typography } from "@material-tailwind/react";
import { TruckIcon } from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { getDeliveries } from "@/store/slices/newDeliverySlice";
import moment from "moment";
import { Link } from "react-router-dom";
import ShowLoader from "@/components/Loader";
import { useTranslation } from "react-i18next";

export function NewDeliveries() {
  const dispatch = useDispatch();
  const { t } = useTranslation("delivery");

  const { deliveries, list_loading } = useSelector((state) => state.delivery);
  const orders = deliveries?.orders ?? [];


  useEffect(() => {
    dispatch(getDeliveries({ type: "pending", page: 1 }));
  }, [dispatch]);

  const initialLoading = list_loading && orders.length === 0;

  return (
    <div className="w-full max-w-7xl mx-auto py-6 sm:py-10 px-4 sm:px-6 space-y-6 sm:space-y-8">
      {initialLoading && <ShowLoader />}

      <Typography
        variant="h4"
        className="text-[#065C36] font-bold text-xl sm:text-3xl"
      >
        {t("new_deliveries_title")} ({orders.length === 0 ? 0 : orders.length.toString().padStart(2, '0')})
      </Typography>

      {!initialLoading && orders.length === 0 ? (
        <div className="text-center py-16 text-gray-500 rounded-xl border border-dashed border-gray-200 bg-white">
          {t("no_new_deliveries")}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {orders.map((order, idx) => {
            const deadline = order?.deliveries?.[0]?.deadline;
            const delivery = order?.deliveries?.[0];
            const priority = delivery?.priority?.toLowerCase();
            const deadlineLabel =
              deadline && moment(deadline).isValid()
                ? moment(deadline).format("DD/MM/YYYY")
                : "—";

            return (
              <Card
                key={order?.id ?? order?.order_id ?? idx}
                className="shadow-sm border border-gray-100 hover:shadow-md transition-shadow bg-gradient-to-r from-[rgba(253,243,221,1)] to-[rgba(255,255,255,1)]"
              >
                <CardBody className="p-4 sm:p-5">
                  <div className="flex justify-between items-start gap-2 mb-4">
                    <Typography
                      variant="h6"
                      color="blue-gray"
                      className="font-bold text-base sm:text-lg"
                    >
                      #{order.order_id}
                    </Typography>

                    <div className="flex flex-col items-end gap-2">
                      {priority && (
                        <div className="px-3 py-1 rounded-full bg-[#E8F1EE] flex items-center justify-center whitespace-nowrap">
                          <span className="text-[10px] font-medium text-[#065C36] capitalize">
                            {t("priority")}: {priority}
                          </span>
                        </div>
                      )}

                      <Typography className="text-xs text-gray-400 font-medium">
                        {deadlineLabel}
                      </Typography>
                    </div>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex gap-3 min-w-0">
                      <img
                        src="/img/icons/Container-pin.png"
                        alt=""
                        className="h-5 w-5 shrink-0 mt-0.5"
                      />
                      <Typography className="text-sm text-gray-600 font-medium break-words">
                        {order?.deliveries?.[0]?.rfq_product_vendor
                          ?.pickup_location || "—"}
                      </Typography>
                    </div>

                    <div className="flex gap-3 min-w-0">
                      <div className="h-5 w-5 flex items-center justify-center shrink-0 mt-0.5">
                        <img
                          src="/img/icons/Container.png"
                          alt=""
                          className="w-5 h-5"
                        />
                      </div>
                      <Typography className="text-sm text-gray-600 font-medium break-words">
                        {order?.deliveries?.[0]?.delivery_address || "—"}
                      </Typography>
                    </div>
                    <div className="flex gap-3">
                      <img
                        src="/img/icons/Container-1.png"
                        alt=""
                        className="h-5 w-5 shrink-0 mt-0.5"
                      />
                      <Typography className="text-sm text-gray-600 font-medium">
                        {t("items_count", { count: order?.items_count ?? "—" })}
                      </Typography>
                    </div>
                  </div>

                  <div className="flex flex-wrap justify-between items-center gap-2">
                    <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFFFFF] max-w-full">
                      {order?.is_installation === "yes" && (
                        <>
                          <img
                            src="/img/icons/Container-3.png"
                            alt=""
                            className="h-3 w-3 shrink-0"
                          />
                          <span className="text-[10px] font-bold text-[#2F302C] truncate">
                            {t("delivery_and_installation")}
                          </span>
                        </>
                      )}
                      {order?.is_installation === "no" && (
                        <>
                          <TruckIcon className="h-4 w-4 text-[#F4B11F] shrink-0" />
                          <span className="text-[10px] font-bold text-[#F4B11F]">
                            {t("delivery")}
                          </span>
                        </>
                      )}
                    </div>
                    <Link
                      to={`/delivery-partner/new-delivery/${order?.id}`}
                      className="text-xs font-bold text-[#065C36] underline shrink-0"
                    >
                      {t("view_more")}
                    </Link>
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default NewDeliveries;
